/**
 * MyCraftzPreview — premium product showcase section.
 *
 * Position on the homepage: between "Chapters Within" and the Footer.
 *
 * This is NOT the live MyCraftz app. It is an interactive preview that
 * answers one question for a first-time visitor: "Why would I pay for
 * MyCraftz™?" — by walking them through the 5-stage transformation arc
 * (Jigyasa → Bodha → Sankalpa → Sadhana → Siddhi) using a single
 * worked example (Atomic Habits → Identity-Based Habits).
 *
 * Reference stage labels mirror /app/frontend/src/pages/JourneyPage.jsx
 * (single source of truth). Do NOT rename or invent new terminology.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowRight, BookMarked, BrainCircuit, HandMetal, Flower2,
  Check, Quote, Clock, Target, AlertTriangle, ScrollText,
  Sparkles, Calendar, Sun,
} from "lucide-react";
import "../styles/MyCraftzJourneyTeaser.css";

function MeditatorIcon({ size = 22, strokeWidth = 1.6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"
      stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="16" cy="9" r="3.2" />
      <path d="M11 14.5c1.5-1 3-1.4 5-1.4s3.5.4 5 1.4" />
      <path d="M9 20c2-3 4-3.6 7-3.6s5 .6 7 3.6" />
      <path d="M5 25c3-2.5 7-3 11-3s8 .5 11 3" />
    </svg>
  );
}

const STAGES = [
  { num: 1, key: "jigyasa",  name: "JIGYASA",  sub: "Curiosity",      icon: BookMarked },
  { num: 2, key: "bodha",    name: "BODHA",    sub: "Understanding",  icon: BrainCircuit },
  { num: 3, key: "sankalpa", name: "SANKALPA", sub: "Commitment",     icon: HandMetal },
  { num: 4, key: "sadhana",  name: "SADHANA",  sub: "Practice",       icon: MeditatorIcon },
  { num: 5, key: "siddhi",   name: "SIDDHI",   sub: "Transformation", icon: Flower2 },
];

const VALUE_POINTS = [
  "Converts reading into action",
  "Creates 30-day execution blueprints",
  "Tracks consistency and progress",
  "Captures reflections and learning",
  "Helps transform ideas into habits",
];

/* ────────────────────────────────────────────
   Stage-specific preview cards
   ──────────────────────────────────────────── */

function JigyasaCard() {
  return (
    <div className="mct-card mct-card--jigyasa" data-testid="mct-preview-jigyasa">
      <div className="mct-card-eyebrow">A BOOK SPARKS YOU</div>
      <div className="mct-jig-row">
        <div className="mct-jig-spine" aria-hidden="true">
          <span className="mct-jig-spine-title">ATOMIC<br/>HABITS</span>
          <span className="mct-jig-spine-author">JAMES CLEAR</span>
        </div>
        <div className="mct-jig-body">
          <p className="mct-jig-book">Atomic Habits — <em>James Clear</em></p>
          <p className="mct-jig-idea-label">SELECTED IDEA</p>
          <p className="mct-jig-idea">Identity-Based Habits</p>
          <blockquote className="mct-jig-quote">
            <Quote size={14} className="mct-quote-icon" aria-hidden="true" />
            <span>Your identity shapes your habits, and your habits shape your identity.</span>
          </blockquote>
          <span className="mct-jig-cta" data-testid="mct-jigyasa-cta">
            <Check size={12} /> CHOOSE THIS IDEA
          </span>
        </div>
      </div>
    </div>
  );
}

function BodhaCard() {
  return (
    <div className="mct-card mct-card--bodha" data-testid="mct-preview-bodha">
      <div className="mct-card-eyebrow">YOU UNDERSTAND IT DEEPLY</div>
      <div className="mct-bodha-glyph" aria-hidden="true">
        <BrainCircuit size={32} strokeWidth={1.3} />
      </div>
      <p className="mct-bodha-text">
        Small habits become powerful when they are connected to the
        <em> identity </em>you want to build. You don&apos;t read to consume —
        you read to <strong>become</strong>.
      </p>
      <ul className="mct-bodha-key">
        <li><Sparkles size={12} /> Every action is a vote for the kind of person you wish to be.</li>
        <li><Sparkles size={12} /> Behaviour follows belief, not the other way around.</li>
        <li><Sparkles size={12} /> The goal is not to read the book. The goal is to become the chapter.</li>
      </ul>
    </div>
  );
}

function SankalpaCard() {
  return (
    <div className="mct-card mct-card--sankalpa" data-testid="mct-preview-sankalpa">
      <div className="mct-card-eyebrow">YOU COMMIT TO ONE ACTION</div>
      <div className="mct-sankalpa-form">
        <div className="mct-form-row">
          <span className="mct-form-label"><Sun size={12} /> TRIGGER</span>
          <span className="mct-form-value">After brushing my teeth.</span>
        </div>
        <div className="mct-form-row">
          <span className="mct-form-label"><Target size={12} /> ACTION</span>
          <span className="mct-form-value">Read 2 pages.</span>
        </div>
        <div className="mct-form-row">
          <span className="mct-form-label"><Calendar size={12} /> DURATION</span>
          <span className="mct-form-value">30 Days.</span>
        </div>
      </div>
      <p className="mct-sankalpa-seal">
        Sealed with intent — <em>Sankalpa</em>.
      </p>
    </div>
  );
}

const CRAFTPRINT_ROWS = [
  { label: "SUTRA",    sanskrit: "सूत्र",   value: "Become the person first." },
  { label: "NIMITTA",  sanskrit: "निमित्त", value: "After brushing teeth." },
  { label: "KRIYA",    sanskrit: "क्रिया",  value: "Read 2 pages." },
  { label: "KALA",     sanskrit: "काल",    value: "30 Days." },
  { label: "LAKSHYA",  sanskrit: "लक्ष्य",  value: "20+ successful days." },
  { label: "PHALA",    sanskrit: "फल",     value: "Improved reading consistency." },
  { label: "VIGHNA",   sanskrit: "विघ्न",  value: "Forgetfulness." },
  { label: "UPAYA",    sanskrit: "उपाय",   value: "Set reminder. Never miss twice." },
];

function SadhanaCard() {
  return (
    <div className="mct-card mct-card--sadhana" data-testid="mct-preview-sadhana">
      <div className="mct-card-eyebrow">YOUR CRAFTPRINT<sup>™</sup> — THE BLUEPRINT</div>
      <p className="mct-sadhana-title">EXECUTION BLUEPRINT</p>
      <div className="mct-craftprint-grid">
        {CRAFTPRINT_ROWS.map((r) => (
          <div className="mct-cp-row" key={r.label}>
            <div className="mct-cp-tag">
              <span className="mct-cp-label">{r.label}</span>
              <span className="mct-cp-sanskrit indic">{r.sanskrit}</span>
            </div>
            <div className="mct-cp-value">{r.value}</div>
          </div>
        ))}
      </div>
      <p className="mct-sadhana-foot">
        <ScrollText size={12} /> This is what separates reading from transformation.
      </p>
    </div>
  );
}

function SiddhiCard() {
  const days = 7, total = 30;
  const pct = (days / total) * 100;
  // SVG ring math
  const R = 52, C = 2 * Math.PI * R;
  const dash = (pct / 100) * C;

  return (
    <div className="mct-card mct-card--siddhi" data-testid="mct-preview-siddhi">
      <div className="mct-card-eyebrow">TRANSFORMATION TAKES ROOT</div>
      <div className="mct-siddhi-row">
        <div className="mct-dial-wrap" aria-hidden="true">
          <svg viewBox="0 0 120 120" className="mct-dial">
            <defs>
              <linearGradient id="mct-brass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F2C470" />
                <stop offset="55%" stopColor="#DA9B4A" />
                <stop offset="100%" stopColor="#8B5A22" />
              </linearGradient>
            </defs>
            <circle cx="60" cy="60" r={R} className="mct-dial-track" />
            <circle
              cx="60" cy="60" r={R}
              className="mct-dial-fill"
              strokeDasharray={`${dash} ${C - dash}`}
              transform="rotate(-90 60 60)"
            />
            <text x="60" y="58" textAnchor="middle" className="mct-dial-num">{days}</text>
            <text x="60" y="76" textAnchor="middle" className="mct-dial-den">/ {total} DAYS</text>
          </svg>
        </div>
        <div className="mct-siddhi-meta">
          <div className="mct-streak">
            <span className="mct-streak-num">7</span>
            <span className="mct-streak-label">DAY STREAK</span>
          </div>
          <div className="mct-streak-dots" aria-hidden="true">
            {Array.from({ length: 30 }).map((_, i) => (
              <span key={`siddhi-dot-${i + 1}`} className={`mct-dot ${i < days ? "is-on" : ""}`} />
            ))}
          </div>
          <blockquote className="mct-reflection">
            <Quote size={12} className="mct-quote-icon" aria-hidden="true" />
            <span>I no longer wait for motivation. Reading has become automatic.</span>
            <em>— Day 7 reflection</em>
          </blockquote>
        </div>
      </div>
    </div>
  );
}

const STAGE_CARDS = {
  jigyasa: JigyasaCard,
  bodha: BodhaCard,
  sankalpa: SankalpaCard,
  sadhana: SadhanaCard,
  siddhi: SiddhiCard,
};

export default function MyCraftzJourneyTeaser() {
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState("jigyasa");
  const activeIdx = STAGES.findIndex((s) => s.key === activeKey);
  const ActiveCard = STAGE_CARDS[activeKey] || JigyasaCard;

  return (
    <section
      className="mct-section"
      id="mycraftz-preview"
      aria-label="MyCraftz Premium Preview"
      data-testid="mycraftz-journey-teaser"
    >
      <header className="mct-header">
        <span className="mct-eyebrow">— THE TRANSFORMATION ENGINE —</span>
        <h2 className="mct-title">MYCRAFTZ<sup className="mct-tm">™</sup></h2>
        <p className="mct-subtitle">Transform Reading Into Action.</p>
        <p className="mct-supporting">
          Reading creates awareness. Action creates transformation.<br/>
          MyCraftz converts the wisdom of great books into structured 30-day
          behavioural blueprints — practiced, tracked, and refined.
        </p>
        <div className="mct-flourish" aria-hidden="true"><span /><em>✦</em><span /></div>
      </header>

      {/* Five-stage clickable ribbon */}
      <ol className="mct-ribbon" role="tablist" aria-label="MyCraftz journey stages" data-testid="mct-stage-ribbon">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const isActive = s.key === activeKey;
          const isPassed = i < activeIdx;
          return (
            <li key={s.key} className="mct-stage-li">
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`mct-stage ${isActive ? "is-active" : ""} ${isPassed ? "is-passed" : ""}`}
                onClick={() => setActiveKey(s.key)}
                data-testid={`mct-stage-${s.key}`}
              >
                <div className="mct-medallion" aria-hidden="true">
                  <span className="mct-medallion-num">{String(s.num).padStart(2, "0")}</span>
                  <span className="mct-medallion-icon"><Icon size={18} strokeWidth={1.6} /></span>
                </div>
                <div className="mct-stage-body">
                  <p className="mct-stage-title">{s.name}</p>
                  <p className="mct-stage-sub">{s.sub}</p>
                </div>
              </button>
              {i < STAGES.length - 1 && (
                <span className="mct-connector" aria-hidden="true">
                  <ArrowRight size={14} />
                </span>
              )}
            </li>
          );
        })}
      </ol>

      {/* Preview card swap */}
      <div className="mct-preview-wrap">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeKey}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mct-preview-inner"
          >
            <ActiveCard />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Value callout */}
      <div className="mct-value" data-testid="mct-value-callout">
        <p className="mct-value-title">WHAT MYCRAFTZ<sup>™</sup> DOES</p>
        <ul className="mct-value-list">
          {VALUE_POINTS.map((p) => (
            <li key={p}><span className="mct-check" aria-hidden="true"><Check size={12} strokeWidth={3} /></span>{p}</li>
          ))}
        </ul>
      </div>

      {/* Subscription / CTA */}
      <div className="mct-sub" data-testid="mct-subscription">
        <div className="mct-sub-left">
          <p className="mct-sub-brand">MYCRAFTZ<sup>™</sup></p>
          <p className="mct-sub-price"><span className="mct-price-num">₹999</span><span className="mct-price-unit">/month</span></p>
          <p className="mct-sub-tagline">
            Not another reading app.<br/>
            A behavioural implementation system built around books.
          </p>
        </div>
        <div className="mct-sub-right">
          <button
            type="button"
            className="mct-cta mct-cta-secondary"
            onClick={() => navigate("/mycraftz/journey/atomic-habits")}
            data-testid="mct-preview-journey-btn"
          >
            PREVIEW A JOURNEY <ArrowRight size={14} />
          </button>
          <button
            type="button"
            className="mct-cta mct-cta-primary"
            onClick={() => navigate("/mycraftz")}
            data-testid="mct-start-btn"
          >
            START MYCRAFTZ<sup>™</sup> <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
